"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { FileText, Loader2, Plus } from "lucide-react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";

interface BAPBDisclosureTestingProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface MovementRow {
  category: string;
  opening_value: number;
  opening_qty: number;
  acq_value: number;
  acq_qty: number;
  disp_value: number;
  disp_qty: number;
  closing_value: number;
  closing_qty: number;
  change_in_fv_less_costs_to_sell: number;
}

interface BioAstRoot {
  report_date?: string;
  disclosure_testing?: {
    movement_table?: MovementRow[];
  };
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtQty(n: number): string {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function fmtValue(n: number): string {
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function CatCells({
  rows,
  pickQty,
  pickValue,
  valueOnly,
}: {
  rows: MovementRow[];
  pickQty: (r: MovementRow) => number;
  pickValue: (r: MovementRow) => number;
  valueOnly?: boolean;
}) {
  return (
    <>
      {rows.map((r, i) => (
        <React.Fragment key={`${r.category}-${i}`}>
          <td className="border border-white/10 px-3 py-2 text-right tabular-nums">
            {valueOnly ? "—" : fmtQty(pickQty(r))}
          </td>
          <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmtValue(pickValue(r))}</td>
        </React.Fragment>
      ))}
    </>
  );
}

export default function BAPBDisclosureTesting({ onBack }: BAPBDisclosureTestingProps) {
  const [movementRows, setMovementRows] = useState<MovementRow[]>([]);
  const [reportDate, setReportDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const totals = useMemo(() => {
    if (!movementRows.length) return null;
    const sum = movementRows.reduce(
      (acc, r) => ({
        opening_qty: acc.opening_qty + num(r.opening_qty),
        opening_value: acc.opening_value + num(r.opening_value),
        acq_qty: acc.acq_qty + num(r.acq_qty),
        acq_value: acc.acq_value + num(r.acq_value),
        disp_qty: acc.disp_qty + num(r.disp_qty),
        disp_value: acc.disp_value + num(r.disp_value),
        closing_qty: acc.closing_qty + num(r.closing_qty),
        closing_value: acc.closing_value + num(r.closing_value),
        change_fv: acc.change_fv + num(r.change_in_fv_less_costs_to_sell),
      }),
      {
        opening_qty: 0,
        opening_value: 0,
        acq_qty: 0,
        acq_value: 0,
        disp_qty: 0,
        disp_value: 0,
        closing_qty: 0,
        closing_value: 0,
        change_fv: 0,
      },
    );

    const computedClosingValue =
      sum.opening_value + sum.acq_value - sum.disp_value - sum.change_fv;
    const computedClosingQty = sum.opening_qty + sum.acq_qty - sum.disp_qty;

    return {
      ...sum,
      computed_closing_value: computedClosingValue,
      computed_closing_qty: computedClosingQty,
    };
  }, [movementRows]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const jsonResult = await (window as any).sharePointAPI?.downloadClientJson?.(
          "Execution_BAPB_BioAst.json",
        );
        if (jsonResult?.success && jsonResult?.data) {
          const data = jsonResult.data as BioAstRoot;
          setReportDate(
            typeof data.report_date === "string" && data.report_date.trim() ? data.report_date.trim() : null,
          );
          const disc = data.disclosure_testing;
          if (disc?.movement_table && Array.isArray(disc.movement_table)) {
            setMovementRows(disc.movement_table as MovementRow[]);
          } else {
            setMovementRows([]);
          }
        } else {
          setReportDate(null);
          setMovementRows([]);
        }
      } catch {
        // ignore, show partial UI
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Disclosure Testing – Biological Assets other than Bearer Plants
          </h2>
          <p className="text-sm text-white/60">
            Testing adequacy and accuracy of disclosures related to biological assets
          </p>
          {!loading && reportDate && (
            <p className="text-sm text-cyan-300/90 mt-1">Report date (from file): {reportDate}</p>
          )}
        </div>
      </div>

      <Card className="border-white/10 bg-white/5 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-purple-400" />
            Disclosure Testing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading disclosure data...</span>
            </div>
          )}

          {!loading && totals && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-white/90">Movement schedule</h3>
              <p className="text-xs text-white/50">
                From <code className="text-white/70">disclosure_testing.movement_table</code> in
                Execution_BAPB_BioAst.json. Closing balance = opening + acquisitions − sales − change in fair value
                (value); quantity = opening + acquisitions − sales.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm min-w-[640px]">
                  <thead>
                    <tr className="bg-white/10">
                      <th
                        rowSpan={2}
                        className="border border-white/10 px-3 py-2 text-left align-bottom whitespace-nowrap"
                      >
                        Particulars
                      </th>
                      {movementRows.map((r, i) => (
                        <th
                          key={`h-cat-${r.category}-${i}`}
                          colSpan={2}
                          className="border border-white/10 px-3 py-2 text-center font-semibold"
                        >
                          {r.category || `Category ${i + 1}`}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-white/10">
                      {movementRows.map((r, i) => (
                        <React.Fragment key={`h-sub-${r.category}-${i}`}>
                          <th className="border border-white/10 px-2 py-1 text-right text-xs font-normal text-white/80">
                            Qty
                          </th>
                          <th className="border border-white/10 px-2 py-1 text-right text-xs font-normal text-white/80">
                            Value
                          </th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-white/10 px-3 py-2">Opening Balance</td>
                      <CatCells
                        rows={movementRows}
                        pickQty={(r) => num(r.opening_qty)}
                        pickValue={(r) => num(r.opening_value)}
                      />
                    </tr>
                    <tr className="bg-white/5">
                      <td className="border border-white/10 px-3 py-2 font-medium">Add:</td>
                      {movementRows.map((_, i) => (
                        <React.Fragment key={`add-pad-${i}`}>
                          <td className="border border-white/10 px-3 py-2 text-right">—</td>
                          <td className="border border-white/10 px-3 py-2 text-right">—</td>
                        </React.Fragment>
                      ))}
                    </tr>
                    <tr>
                      <td className="border border-white/10 px-3 py-2 pl-6">– Acquisitions</td>
                      <CatCells
                        rows={movementRows}
                        pickQty={(r) => num(r.acq_qty)}
                        pickValue={(r) => num(r.acq_value)}
                      />
                    </tr>
                    <tr className="bg-white/5">
                      <td className="border border-white/10 px-3 py-2 font-medium">Less:</td>
                      {movementRows.map((_, i) => (
                        <React.Fragment key={`less-pad-${i}`}>
                          <td className="border border-white/10 px-3 py-2 text-right">—</td>
                          <td className="border border-white/10 px-3 py-2 text-right">—</td>
                        </React.Fragment>
                      ))}
                    </tr>
                    <tr>
                      <td className="border border-white/10 px-3 py-2 pl-6">– Sales</td>
                      <CatCells
                        rows={movementRows}
                        pickQty={(r) => num(r.disp_qty)}
                        pickValue={(r) => num(r.disp_value)}
                      />
                    </tr>
                    <tr>
                      <td className="border border-white/10 px-3 py-2 pl-6 align-top">
                        – Change in fair value less costs to sell
                        <div className="text-xs text-white/50 font-normal mt-1">(value only)</div>
                      </td>
                      <CatCells
                        rows={movementRows}
                        pickQty={() => 0}
                        pickValue={(r) => num(r.change_in_fv_less_costs_to_sell)}
                        valueOnly
                      />
                    </tr>
                    <tr className="bg-white/15 font-semibold">
                      <td className="border border-white/10 px-3 py-2">
                        Closing Balance
                        <div className="text-xs text-white/60 font-normal mt-1 font-normal">
                          Opening + acquisitions − sales − change in fair value (value); opening + acquisitions − sales
                          (qty)
                        </div>
                      </td>
                      {movementRows.map((r, i) => {
                        const cq = num(r.opening_qty) + num(r.acq_qty) - num(r.disp_qty);
                        const cv =
                          num(r.opening_value) + num(r.acq_value) - num(r.disp_value) - num(r.change_in_fv_less_costs_to_sell);
                        return (
                          <React.Fragment key={`close-${r.category}-${i}`}>
                            <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmtQty(cq)}</td>
                            <td className="border border-white/10 px-3 py-2 text-right tabular-nums">{fmtValue(cv)}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !totals && (
            <p className="text-sm text-white/60">
              No movement data found in Execution_BAPB_BioAst.json. Run IPE / Valuation first.
            </p>
          )}

          <div className="space-y-4">
            <Label className="text-sm text-white">
              Has the entity disclosed the following in its financial statements:
            </Label>
            <ul className="list-disc pl-6 space-y-2 text-sm text-white/80">
              <li>
                The aggregate gain or loss arising during the current period on initial recognition
                of biological assets and agricultural produce and from the change in fair value less
                costs to sell of biological assets.
              </li>
              <li>
                The nature of its activities involving each group of biological assets; and
              </li>
              <li>
                Non-financial measures or estimates of the physical quantities of:
                <br />
                (a) each group of the entity’s biological assets at the end of the period; and
                <br />
                (b) output of agricultural produce during the period.
              </li>
              <li>
                An entity shall disclose:
                <br />
                (a) the existence and carrying amounts of biological assets whose title is
                restricted, and the carrying amounts of biological assets pledged as security for
                liabilities;
                <br />
                (b) the amount of commitments for the development or acquisition of biological
                assets; and
                <br />
                (c) financial risk management strategies related to agricultural activity.
              </li>
            </ul>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
